import { NAV_GROUPS } from "@/features/auth/lib/nav-groups";
import { accessLevel } from "@/features/identity-hub/lib/permission-access";
import {
  FEATURE_AREAS,
  featureAreaKeys,
  type FeatureArea,
} from "@/features/identity-hub/lib/permission-catalog";

/** Header dotted slugs vs kebab-case catalog ids. */
const NAV_SLUG_TO_AREA_ID: Record<string, string> = {
  "marketplace.admin": "marketplace-admin",
  "marketplace.orders": "marketplace-orders",
};

/** Catalog areas with no header link. Order status time sits under Script delivery. */
const EXTRA_MANAGE_AREA_IDS = ["mcp", "order-status-time"] as const;

/** Partner navigation is tenant-specific, but its grant belongs with Marketplace. */
const EXTRA_MAKE_AREA_IDS = ["marketplace-partner"] as const;

/** Ask Nexus opens from a header button, not a nav link, and answers questions about the data. */
const EXTRA_EXPLORE_AREA_IDS = ["ask-nexus"] as const;

const BETA_AREA_ID = "beta-features";

export function catalogAreaIdFromNavSlug(navSlug: string): string {
  return NAV_SLUG_TO_AREA_ID[navSlug] ?? navSlug;
}

export interface PermissionNavGroup {
  id: string;
  labelKey: string;
  label: string;
  areas: FeatureArea[];
}

function areasById(areas: FeatureArea[]): Map<string, FeatureArea> {
  return new Map(areas.map((area) => [area.id, area]));
}

function resolveAreas(
  ids: readonly string[],
  byId: Map<string, FeatureArea>,
): FeatureArea[] {
  const resolved: FeatureArea[] = [];
  for (const id of ids) {
    const area = byId.get(id);
    if (area) resolved.push(area);
  }
  return resolved;
}

/** Nest FEATURE_AREAS under Make / Explore / Manage / Beta to match the header. */
export function permissionNavGroups(
  areas: FeatureArea[] = FEATURE_AREAS,
): PermissionNavGroup[] {
  const byId = areasById(areas);

  const groups: PermissionNavGroup[] = NAV_GROUPS.map((group) => {
    const areaIds = group.pages.map(catalogAreaIdFromNavSlug);
    if (group.slug === "make") areaIds.push(...EXTRA_MAKE_AREA_IDS);
    if (group.slug === "explore") areaIds.push(...EXTRA_EXPLORE_AREA_IDS);
    if (group.slug === "manage") areaIds.push(...EXTRA_MANAGE_AREA_IDS);
    return {
      id: group.slug,
      labelKey: group.labelKey,
      label: group.label,
      areas: resolveAreas(areaIds, byId),
    };
  });

  const beta = byId.get(BETA_AREA_ID);
  if (beta) {
    groups.push({
      id: BETA_AREA_ID,
      labelKey: "nav.betaFeatures",
      label: "Beta Features",
      areas: [beta],
    });
  }

  return groups;
}

export function permissionNavGroupKeys(group: PermissionNavGroup): string[] {
  return group.areas.flatMap((area) => featureAreaKeys(area));
}

export function assignedFeatureAreaIds(
  areas: FeatureArea[] = FEATURE_AREAS,
): string[] {
  return permissionNavGroups(areas).flatMap((group) =>
    group.areas.map((area) => area.id),
  );
}

export interface PermissionEditorSection {
  id: string;
  labelKey: string;
  label: string;
  /** Grantable areas only; grouping-only parents (Beta Features) are replaced by their children. */
  areas: FeatureArea[];
}

function grantableAreas(areas: FeatureArea[]): FeatureArea[] {
  return areas.flatMap((area) =>
    area.pagePerm || (area.actionGroups ?? []).length > 0 ? [area] : grantableAreas(area.children ?? []),
  );
}

/** Nav groups flattened to one level of areas, the shape the permission editor rail renders. */
export function permissionEditorSections(
  areas: FeatureArea[] = FEATURE_AREAS,
): PermissionEditorSection[] {
  return permissionNavGroups(areas).map((group) => ({
    id: group.id,
    labelKey: group.labelKey,
    label: group.label,
    areas: grantableAreas(group.areas),
  }));
}

export function areaAccessCounts(
  selected: ReadonlySet<string>,
  areas: FeatureArea[] = FEATURE_AREAS,
): Record<"full" | "partial" | "none", number> {
  const counts = { full: 0, partial: 0, none: 0 };
  for (const section of permissionEditorSections(areas)) {
    for (const area of section.areas) {
      counts[accessLevel(selected, featureAreaKeys(area))] += 1;
    }
  }
  return counts;
}
